import api from '../utils/api';

/**
 * @typedef {Object} LessonContent
 * @property {string} [articleText]
 * @property {string} [videoUrl]
 * @property {string} [vdocipherVideoId]
 * @property {Array<{name: string, url: string, fileType: string, size: number}>} [attachments]
 */

/**
 * @typedef {Object} Lesson
 * @property {string} _id
 * @property {string} title
 * @property {string} [description]
 * @property {LessonContent|string} [content]
 * @property {number} duration
 * @property {boolean} isFree
 * @property {boolean} isPublished
 * @property {string} course
 * @property {string} module
 * @property {number} order
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateLessonData
 * @property {string} title
 * @property {string} [description]
 * @property {Object} [content]
 * @property {number} [duration]
 * @property {boolean} [isFree]
 * @property {boolean} [isPublished]
 * @property {string} module
 * @property {number} [order]
 */

export const lessonService = {
  // ==================
  // SEARCH ROUTES
  // ==================

  /**
   * Search and filter lessons
   * @param {Object} params 
   * @returns {Promise<{status: string, count: number, lessons: Lesson[]}>}
   */
  searchAndFilterLessons: async (params = {}) => {
    const response = await api.get('/api/lessons/search', { params });
    return response;
  },

  /**
   * Quick search lessons by name
   * @param {string} query 
   * @returns {Promise<{status: string, lessons: Lesson[]}>}
   */
  searchLessonsByName: async (query) => {
    const response = await api.get(`/api/lessons/search/quick?q=${query}`);
    return response;
  },

  // ==================
  // PUBLIC/ACCESS ROUTES
  // ==================

  /**
   * Get all lessons
   * @returns {Promise<{status: string, count: number, lessons: Lesson[]}>}
   */
  getAllLessons: async () => {
    const response = await api.get('/api/lessons');
    return response;
  },

  /**
   * Get lessons by course
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, lessons: Lesson[]}>}
   */
  getLessonsByCourse: async (courseId) => {
    const response = await api.get(`/api/lessons/course/${courseId}`);
    return response;
  },

  /**
   * Get lesson by ID
   * @param {string} id 
   * @returns {Promise<{status: string, lesson: Lesson}>}
   */
  getLessonById: async (id) => {
    const response = await api.get(`/api/lessons/${id}`);
    return response;
  },

  // ==================
  // INSTRUCTOR/ADMIN ROUTES
  // ==================

  /**
   * Create lesson
   * @param {CreateLessonData} lessonData 
   * @returns {Promise<{status: string, lesson: Lesson}>}
   */
  createLesson: async (lessonData) => {
    const response = await api.post('/api/lessons', lessonData);
    return response;
  },

  /**
   * Update lesson
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, lesson: Lesson}>}
   */
  updateLesson: async (id, updates) => {
    const response = await api.put(`/api/lessons/${id}`, updates);
    return response;
  },

  /**
   * Delete lesson
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteLesson: async (id) => {
    const response = await api.delete(`/api/lessons/${id}`);
    return response;
  },

  /**
   * Move a lesson to a target module and/or reorder within its current module.
   *
   * @param {string} lessonId - MongoId of the lesson being moved.
   * @param {{ targetModuleId: string, newOrder: number }} payload
   *   - targetModuleId: MongoId of the destination module (may equal the lesson's current module for in-place reorder).
   *   - newOrder: 1-based insertion position in the target module's lessons list.
   * @returns {Promise<{status: string, message: string, lesson: Lesson, module?: Module, sourceModule?: Module, targetModule?: Module}>}
   *   On same-module reorder: response shape is `{ status, message, lesson, module }`.
   *   On cross-module move: response shape is `{ status, message, lesson, sourceModule, targetModule }`.
   */
  moveLesson: async (lessonId, payload) => {
    const response = await api.post(`/api/lessons/${lessonId}/move`, payload);
    return response;
  },

  // ==================
  // FAVORITES
  // ==================

  /**
   * Add lesson to favorites
   * @param {string} lessonId 
   * @returns {Promise<{status: string, message: string}>}
   */
  addToFavorites: async (lessonId) => {
    const response = await api.post('/api/favorite-lessons', { lessonId });
    return response;
  },

  /**
   * Get my favorite lessons
   * @returns {Promise<{status: string, count: number, favorites: Object[]}>}
   */
  getMyFavoriteLessons: async () => {
    const response = await api.get('/api/favorite-lessons/my-favorites');
    return response;
  },

  /**
   * Check if lesson is favorited
   * @param {string} lessonId 
   * @returns {Promise<{status: string, isFavorited: boolean}>}
   */
  checkIfLessonFavorited: async (lessonId) => {
    const response = await api.get(`/api/favorite-lessons/check/${lessonId}`);
    return response;
  },

  /**
   * Get user's favorite lessons (Admin)
   * @param {string} userId 
   * @returns {Promise<{status: string, count: number, favorites: Object[]}>}
   */
  getUserFavoriteLessons: async (userId) => {
    const response = await api.get(`/api/favorite-lessons/user/${userId}`);
    return response;
  },

  /**
   * Get user's favorite lessons by course
   * @param {string} userId 
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, favorites: Object[]}>}
   */
  getUserFavoriteLessonsByCourse: async (userId, courseId) => {
    const response = await api.get(`/api/favorite-lessons/user/${userId}/course/${courseId}`);
    return response;
  },

  /**
   * Remove lesson from favorites
   * @param {string} lessonId 
   * @returns {Promise<{status: string, message: string}>}
   */
  removeFromFavorites: async (lessonId) => {
    const response = await api.delete(`/api/favorite-lessons/${lessonId}`);
    return response;
  },
};
